// Portfolio Administration v1.0 PR 5 — shared detail-page primitive for
// the cross-portfolio Vendors + Local Enforcers registry pages. Wraps
// the sticky header + EntityTabBar pattern (same as LoanDetailPage)
// behind a tiny set of props so each registry detail page composes its
// own header content + tab items + content router.

import type { ReactNode } from "react";
import {
  EntityTabBar,
  type EntityTabItem,
} from "@/components/layout/EntityTabBar";

export interface RegistryDetailLayoutProps<TKey extends string> {
  testIdPrefix: string;
  /** Sticky header content rendered above the tab strip. */
  header: ReactNode;
  /** Tab strip aria-label, e.g. "Vendor sections". */
  tabsAriaLabel: string;
  tabItems: ReadonlyArray<EntityTabItem<TKey>>;
  activeTab: TKey;
  onTabChange: (key: TKey) => void;
  /** Active tab body. */
  children: ReactNode;
}

export function RegistryDetailLayout<TKey extends string>({
  testIdPrefix,
  header,
  tabsAriaLabel,
  tabItems,
  activeTab,
  onTabChange,
  children,
}: RegistryDetailLayoutProps<TKey>) {
  return (
    <div
      className="flex flex-col gap-space-3"
      data-testid={`${testIdPrefix}-detail-page`}
    >
      {header}
      <EntityTabBar
        ariaLabel={tabsAriaLabel}
        testIdPrefix={testIdPrefix}
        items={tabItems}
        value={activeTab}
        onChange={onTabChange}
      />
      <div className="pt-space-4">{children}</div>
    </div>
  );
}

/**
 * Hook helper for `?tab=` URL persistence used by both registry detail
 * pages. Returns a tuple of [activeTab, setTab]. Falls back to the first
 * allowed tab when the query string is missing or invalid.
 */
export function useRegistryTabState<TKey extends string>(
  allowedTabs: readonly TKey[],
  params: URLSearchParams,
  setParams: (next: URLSearchParams, options?: { replace?: boolean }) => void,
): [TKey, (next: TKey) => void] {
  const raw = params.get("tab");
  const active: TKey = (allowedTabs as readonly string[]).includes(raw ?? "")
    ? (raw as TKey)
    : allowedTabs[0];
  const setTab = (next: TKey) => {
    const updated = new URLSearchParams(params);
    if (next === allowedTabs[0]) updated.delete("tab");
    else updated.set("tab", next);
    setParams(updated, { replace: true });
  };
  return [active, setTab];
}
